import { useMemo } from "react";
import { Link } from "wasp/client/router";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import {
  getMoodEmoji,
  getMoodLabel,
  getPerformanceLabel,
  getPerformanceColor,
  formatSessionDuration,
  isTradeInSession,
  sessionChartWindow,
} from "../../../trading-sessions/sessionUtils";
import { SessionErrorBadges } from "../../../trading-sessions/SessionErrorBadges";
import { SessionChart } from "../../../trading-sessions/SessionChart";
import { Youtube, ArrowUpRight } from "lucide-react";

function netPnl(trade: any): number {
  return (trade.pnl ?? 0) + (trade.commission ?? 0) + (trade.swaps ?? 0);
}

function fmtMoney(v: number): string {
  const abs = Math.abs(v);
  const prefix = v < 0 ? "-$" : "$";
  if (abs >= 1000) return `${prefix}${(abs / 1000).toFixed(1)}k`;
  return `${prefix}${abs.toFixed(0)}`;
}

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function sessionTrades(session: any, trades: any[]): any[] {
  if (!session.startedAt || !session.endedAt) return [];
  // Attach trades to the session by their ENTRY time (see isTradeInSession).
  return trades
    .filter((tr) => isTradeInSession(tr, session))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

export default function LastSessionWidget() {
  const { sessions, statsTradesList, accounts, isDark, t } = useDashboardData();

  const last = useMemo(() => {
    const closed = (sessions ?? []).filter((s: any) => !s.isActive && s.startedAt && s.endedAt);
    closed.sort((a: any, b: any) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
    return closed[0] ?? null;
  }, [sessions]);

  // Trades inside the session window — feeds both the headline P&L and the chart.
  const trades = useMemo(
    () => (last ? sessionTrades(last, statsTradesList ?? []) : []),
    [last, statsTradesList],
  );
  const pnl = useMemo(
    () => Math.round(trades.reduce((sum, tr) => sum + netPnl(tr), 0) * 100) / 100,
    [trades],
  );

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader label={t.dashboard.widgets.lastSession} isDark={isDark} compact />
      <CardContent className="px-4 pb-4 pt-2 flex-1 flex flex-col gap-3">
        {!last ? (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            {t.session.lastSessionEmpty}
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-semibold leading-tight">
                  {last.name || formatDate(last.startedAt)}
                </p>
              </div>
              <Link
                to="/session/:sessionId"
                params={{ sessionId: last.id }}
                className="flex shrink-0 items-center gap-1 text-xs text-primary hover:underline"
              >
                {t.session.lastSessionOpen}
                <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              {last.mood && (
                <span className="rounded-md bg-muted px-2 py-0.5">
                  {getMoodEmoji(last.mood)} {getMoodLabel(last.mood, t)}
                </span>
              )}
              {last.performance && (
                <span className={`rounded-md bg-muted px-2 py-0.5 ${getPerformanceColor(last.performance)}`}>
                  {getPerformanceLabel(last.performance, t)}
                </span>
              )}
              <span className="rounded-md bg-muted px-2 py-0.5 text-muted-foreground">
                {formatSessionDuration(last.actualDuration)}
              </span>
              {last.videoUrl && (
                <span className="flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-muted-foreground">
                  <Youtube className="h-3 w-3" /> {t.session.lastSessionVideo}
                </span>
              )}
            </div>

            <div className="flex items-end justify-between gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.session.lastSessionPnl}</p>
                <p className={`font-mono text-2xl font-semibold ${pnl > 0 ? "text-pnlPositive" : pnl < 0 ? "text-pnlNegative" : "text-muted-foreground"}`}>
                  {fmtMoney(pnl)}
                </p>
              </div>
              <div className="text-right text-xs text-muted-foreground space-y-0.5">
                {last.maxRunup != null && (
                  <p>{t.session.lastSessionRunup}: <span className="font-mono text-pnlPositive">{fmtMoney(last.maxRunup)}</span></p>
                )}
                {last.maxDrawdown != null && (
                  <p>{t.session.lastSessionDrawdown}: <span className="font-mono text-pnlNegative">{fmtMoney(last.maxDrawdown)}</span></p>
                )}
              </div>
            </div>

            <SessionErrorBadges errors={(last as any).errors} />

            <div className="flex-1 min-h-0 pb-1">
              <SessionChart
                session={sessionChartWindow(last, trades)}
                trades={trades}
                accounts={(accounts ?? []) as any[]}
                isDark={isDark}
                fill
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
